import type { BannerConfig, Choice, ConsentState, Regime } from './types.js';

/**
 * 동의 상태를 **브라우저에 보관**한다.
 *
 * 세 안(로컬만 / 서버 적재 / 절충) 중 무엇을 고르든 **로컬 저장은 공통**이다.
 * 서버 기록은 그 위에 얹는 것이라, 보관처 결정(명세 §2)을 기다리지 않고
 * 여기까지는 지을 수 있다. 서버로 보내는 일은 `Recorder` 가 맡는다.
 */

const KEY = 'ray-cookie-consent';

/** 동의의 유효기간. 넘으면 다시 묻는다. */
const MAX_AGE_DAYS = 365;

export function load(): ConsentState | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return null;
    const v = JSON.parse(raw) as Partial<ConsentState>;
    if (
      typeof v.decidedAt !== 'string' ||
      typeof v.purposesVersion !== 'string' ||
      typeof v.choices !== 'object' ||
      v.choices === null
    ) {
      return null;
    }
    return v as ConsentState;
  } catch {
    // 사생활 보호 모드·저장소 차단·따옴표 깨짐 전부 여기로 온다.
    // **읽기 실패는 「동의하지 않았다」로 본다** — 동의한 것으로 보면
    // 읽을 수 없는 상태에서 쿠키를 심게 된다.
    return null;
  }
}

export function save(state: ConsentState): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
    return true;
  } catch {
    // 저장이 안 되면 다음 방문에 다시 묻게 된다. 귀찮지만 안전한 쪽이다.
    return false;
  }
}

export function clear(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 지우지 못해도 호출자를 깨우지 않는다 */
  }
}

/**
 * 다시 물어야 하는가.
 *
 * 셋 중 하나면 다시 묻는다.
 */
export function needsDecision(
  state: ConsentState | null,
  config: BannerConfig,
  now: Date = new Date(),
): boolean {
  if (state === null) return true;

  // ① 목적 목록이 바뀌었다 — 묻지 않은 목적이 생겼다는 뜻이다.
  //    기존 동의를 그대로 쓰면 **묻지도 않은 것에 동의한 것**이 된다.
  if (state.purposesVersion !== config.purposesVersion) return true;

  // ② 유효기간이 지났다.
  const decided = Date.parse(state.decidedAt);
  if (Number.isNaN(decided)) return true;
  if (now.getTime() - decided > MAX_AGE_DAYS * 86_400_000) return true;

  // ③ 선택이 없는 목적이 있다 — ①로 대부분 걸리지만, 버전을 올리지 않고
  //    목적만 바꾼 경우를 막는다. 선언을 믿되 확인도 한다.
  return config.purposes.some(
    (p) => p.essential !== true && state.choices[p.id] === undefined,
  );
}

/** 그 목적이 허용됐는가. **필수 목적은 늘 참이다.** */
export function allows(
  state: ConsentState | null,
  config: BannerConfig,
  purposeId: string,
): boolean {
  const p = config.purposes.find((x) => x.id === purposeId);
  if (p?.essential === true) return true;
  return state?.choices[purposeId] === 'granted';
}

/** 전부 수락 / 전부 거부 / 개별 선택을 한 모양으로 만든다. */
export function decide(
  config: BannerConfig,
  regime: Regime,
  picks: Readonly<Record<string, Choice>>,
  now: Date = new Date(),
  previous: ConsentState | null = null,
): ConsentState {
  const choices: Record<string, Choice> = {};
  for (const p of config.purposes) {
    // **필수는 선택을 무시하고 granted 다.** 화면에서 끌 수 없으므로
    // 거부가 들어올 수 없고, 들어와도 받지 않는다.
    choices[p.id] = p.essential === true ? 'granted' : (picks[p.id] ?? 'denied');
  }
  return {
    choices,
    regime,
    decidedAt: now.toISOString(),
    purposesVersion: config.purposesVersion,
    // 이미 있으면 **이어 쓴다** — 바꾼 선택이 최초 선택과 같은 주체여야
    // 「철회했다」가 증거가 된다.
    subjectId: typeof previous?.subjectId === 'string' ? previous.subjectId : newSubjectId(),
  };
}

/**
 * 주체 식별자를 만든다. 무작위 UUID 다 — 이메일·전화번호 형태가 아니므로
 * `opaque` 테넌트가 받는다.
 */
function newSubjectId(): string {
  // `randomUUID` 는 보안 컨텍스트에서만 있다. 없다고 **선택 자체가 터지면
  // 안 된다** — 같은 형태를 `getRandomValues` 로 만든다.
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
